import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Loader2, AlertTriangle, X, Users } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import { SECTION_COLOR } from '../objednavky/ui';
import OsaDne from './OsaDne';
import Tyden from './Tyden';
import SeznamRezervaci from './SeznamRezervaci';
import RezervaceDetail from './RezervaceDetail';
import RezervaceFormular from './RezervaceFormular';
import { api, naDatum, posun, pondeli, denCesky, denDlouze, zasahujeDen, odpovidaHledani } from './rezervaceUi';

const POHLEDY = [
    { id: 'den', label: 'Den' },
    { id: 'tyden', label: 'Týden' },
    { id: 'seznam', label: '30 dní' },
];

/**
 * Hosté → Rezervace. Norma §1: seznam · detail (bez třetího sloupce).
 *
 * Přehled přes celou šířku je kalendář — osa dne (řádek = stůl, sloupec =
 * čas), týden po dnech, nebo 30 dní jako seznam. Po výběru rezervace se
 * zúží na řádky po dnech (osa ani týden se do úzkého sloupce nevejdou)
 * a vedle se otevře detail s akcemi a souhrnem hosta. Telefon má vždycky
 * řádky — osa by se tam musela posouvat do boku.
 *
 * Totéž API jako pokladna (`/api/pos/rezervace`). Pokladna při změně
 * vysílá `PosRezervaceZmenena`, ale kanál se liší podle prostředí — OS se
 * proto dorovná sám každou minutu a po návratu do okna.
 *
 * Proklik: `navigateTo('objednavky', { tab: 'rezervace', id, datum })`.
 */
export default function RezervaceModul({ isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole = null, otevritId = null, otevritDatum = null, muzeZakaznici = false, onZakaznik, onObjednavka }) {
    const [pohled, setPohled] = useState('den');
    const [den, setDen] = useState(() => otevritDatum || naDatum(new Date()));
    const [data, setData] = useState({ rezervace: [], sekce: [], provoz: null });
    const [nacita, setNacita] = useState(true);
    const [vybranyId, setVybranyId] = useState(otevritId ? Number(otevritId) : null);
    const [formular, setFormular] = useState(null);   // null | { rezervace: null | {...} }
    const [zprava, setZprava] = useState(null);       // null | { chyba: bool, radky: string[] }
    const [hledani, setHledani] = useState('');

    useEffect(() => {
        if (otevritId) setVybranyId(Number(otevritId));
        if (otevritDatum) { setDen(otevritDatum); setPohled('den'); }
    }, [otevritId, otevritDatum]);

    // Seznam se dívá na měsíc dopředu — kdo ho otevře, hledá „co nás čeká",
    // ne „co je dneska"; na to je pohled Den.
    const [od, doDatum] = useMemo(() => {
        if (pohled === 'den') return [den, den];
        if (pohled === 'tyden') return [pondeli(den), posun(pondeli(den), 6)];
        return [den, posun(den, 30)];
    }, [pohled, den]);

    const nacti = useCallback(async (tise = false) => {
        if (!tise) setNacita(true);
        try {
            setData(await api(`/api/pos/rezervace?od=${od}&do=${doDatum}`));
            if (!tise) setZprava(null);
        } catch (e) {
            setZprava({ chyba: true, radky: [e.message || 'Rezervace se nepodařilo načíst.'] });
        }
        setNacita(false);
    }, [od, doDatum]);

    useEffect(() => { nacti(); }, [nacti]);
    useEffect(() => {
        const t = setInterval(() => nacti(true), 60000);
        const naViditelnost = () => { if (!document.hidden) nacti(true); };
        document.addEventListener('visibilitychange', naViditelnost);
        return () => { clearInterval(t); document.removeEventListener('visibilitychange', naViditelnost); };
    }, [nacti]);

    const vRozsahu = useMemo(
        () => (pohled === 'den' ? data.rezervace.filter(r => zasahujeDen(r, den)) : data.rezervace),
        [data.rezervace, pohled, den],
    );
    const viditelne = useMemo(() => vRozsahu.filter(r => odpovidaHledani(r, hledani)), [vRozsahu, hledani]);
    const platne = vRozsahu.filter(r => r.stav !== 'zrusena');
    const hostu = platne.reduce((n, r) => n + r.pocet_hostu, 0);
    const vybrana = data.rezervace.find(r => r.id === vybranyId) || null;

    const dnesek = naDatum(new Date());
    const krok = pohled === 'tyden' ? 7 : pohled === 'seznam' ? 30 : 1;
    const popisObdobi = pohled === 'den' ? denDlouze(den) : `${denCesky(od)} – ${denCesky(doDatum)}`;

    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const hranice = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const muted = mutedColor;

    const otevrit = (r) => setVybranyId(r.id);
    const prepnoutNaDen = (d) => { setDen(d); setPohled('den'); };

    const sipka = (smer) => (
        <button type="button" onClick={() => setDen(posun(den, smer * krok))} aria-label={smer < 0 ? 'Předchozí' : 'Další'}
            className="flex items-center justify-center rounded-lg"
            style={{ width: isMobile ? 40 : 30, height: isMobile ? 40 : 30, border: `1px solid ${hranice}`, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
            {smer < 0 ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
    );

    const navigaceData = (
        <div className="flex items-center" style={{ gap: 8, minWidth: 0 }}>
            {sipka(-1)}
            {/* Šipky drží u sebe (popisek neroztahovat) — na širokém přehledu by „›" ujela k pravé hraně */}
            <div className="min-w-0" style={{ flex: isMobile ? 1 : '0 1 auto' }}>
                <div className="truncate" style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{popisObdobi}</div>
                <div className="flex items-center" style={{ gap: 4, fontSize: 11.5, color: muted }}>
                    {platne.length} {platne.length === 1 ? 'rezervace' : platne.length < 5 && platne.length > 0 ? 'rezervace' : 'rezervací'} · <Users size={11} />{hostu}
                    {nacita && <Loader2 size={12} className="animate-spin" style={{ marginLeft: 4 }} />}
                </div>
            </div>
            {sipka(1)}
            {den !== dnesek && (
                <button type="button" onClick={() => setDen(dnesek)} className="rounded-lg"
                    style={{ height: isMobile ? 40 : 30, padding: '0 10px', border: `1px solid ${hranice}`, background: 'transparent', color: 'var(--text-primary)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}>
                    Dnes
                </button>
            )}
        </div>
    );

    const seznamPanel = ({ siroky }) => {
        const kalendar = siroky && !isMobile && !hledani.trim();
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <ListToolbar
                    siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                    hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat jméno, telefon, stůl…', placeholderKratky: 'Hledat' }}
                    akce={[{ id: 'nova', label: 'Nová rezervace', Icon: Plus, primarni: true, onClick: () => setFormular({ rezervace: null }) }]}
                    pohled={POHLEDY.map(p => (
                        <PohledPill key={p.id} label={p.label} aktivni={pohled === p.id} onClick={() => setPohled(p.id)}
                            isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                    ))}
                    pod={navigaceData}
                />

                {zprava && (
                    <div className="flex items-start" style={{
                        gap: 8, margin: '10px 12px 0', padding: '8px 10px', borderRadius: 10, flexShrink: 0,
                        background: zprava.chyba ? 'rgba(239,68,68,0.10)' : 'rgba(245,158,11,0.10)',
                        border: `1px solid ${zprava.chyba ? 'rgba(239,68,68,0.3)' : 'rgba(245,158,11,0.3)'}`,
                    }}>
                        <AlertTriangle size={15} style={{ color: zprava.chyba ? '#EF4444' : '#F59E0B', flexShrink: 0, marginTop: 1 }} />
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 2 }}>
                            {zprava.radky.map((t, i) => <div key={i} style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{t}</div>)}
                        </div>
                        <button type="button" onClick={() => setZprava(null)} aria-label="Zavřít" style={{ background: 'none', border: 'none', cursor: 'pointer', color: muted, padding: 0 }}>
                            <X size={14} />
                        </button>
                    </div>
                )}

                <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                    {kalendar && pohled === 'den' && (
                        <OsaDne sekce={data.sekce} rezervace={data.rezervace} provoz={data.provoz} datum={den}
                            borderColor={cara} mutedColor={muted} vybranyId={vybranyId} onOtevrit={otevrit} />
                    )}
                    {kalendar && pohled === 'tyden' && (
                        <Tyden od={od} rezervace={data.rezervace} borderColor={cara} mutedColor={muted}
                            vybranyId={vybranyId} onOtevrit={otevrit} onDen={prepnoutNaDen} />
                    )}
                    {(!kalendar || pohled === 'seznam') && (
                        <SeznamRezervaci rezervace={viditelne} isMobile={isMobile} borderColor={cara} mutedColor={muted}
                            vybranyId={vybranyId} onOtevrit={otevrit}
                            prazdne={hledani.trim() ? 'Nic se neshoduje.' : pohled === 'den' ? 'Na tenhle den nikdo nerezervoval.' : 'V tomhle období nic není.'} />
                    )}
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </div>
            </div>
        );
    };

    const detailPanel = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            {vybrana ? (
                <RezervaceDetail key={vybrana.id} rezervace={vybrana} isDark={isDark} isMobile={isMobile} mutedColor={muted}
                    muzeZakaznici={muzeZakaznici} onZpet={() => setVybranyId(null)}
                    onUpravit={() => setFormular({ rezervace: vybrana })}
                    onZmena={() => nacti(true)} onZakaznik={onZakaznik} onObjednavka={onObjednavka}
                    rezervaDole={isMobile ? rezervaDole : null} />
            ) : (
                <p className="text-sm text-center" style={{ color: muted, padding: 40 }}>
                    {nacita ? 'Načítám…' : 'Rezervace v tomhle období není — možná se přesunula nebo smazala.'}
                </p>
            )}
        </div>
    );

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} />

            {formular && (
                <RezervaceFormular
                    rezervace={formular.rezervace}
                    datum={den}
                    provoz={data.provoz}
                    isDark={isDark}
                    onZavrit={() => setFormular(null)}
                    onUlozeno={(varovani, serie, ulozena) => {
                        // Kolik termínů série vzniklo není varování, ale potvrzení —
                        // do stejného pruhu ale patří, jinak by o tom nikdo nevěděl.
                        const radky = [
                            ...(serie ? [`Založeno ${serie.pocet} termínů, poslední ${denCesky(serie.do)}.`] : []),
                            ...(varovani || []),
                        ];
                        setZprava(radky.length ? { chyba: false, radky } : null);
                        setFormular(null);
                        if (ulozena?.id) setVybranyId(ulozena.id);
                        // Uložená mimo zobrazené období — přejít na její den, ať je detail vidět
                        if (ulozena?.datum && (ulozena.datum < od || ulozena.datum > doDatum)) setDen(ulozena.datum);
                        nacti(true);
                    }}
                />
            )}
        </div>
    );
}
